/* eslint-disable @typescript-eslint/naming-convention */
import { useDomainMessage, useDomainMessages } from '../domain/intl'
import {
  type ApprovalRequestData,
  type WhitelabelSiteData,
  WhitelabelSiteWorkflowState,
} from '../../components/utils/Onboarding.types'
import { useHasModifyAccess } from '../common.utils'

export const WEB_ADMIN_STEP_MAPPING = {
  IMPORT_EXISTING_SITE: 0,
  ADDITIONAL_BRANDING: 1,
  DOMAIN: 2,
  FULFILLMENT: 3,
  IDENTITY: 4,
  PRIVACY: 5,
  CUSTOMER_EXPERIENCE: 6,
  CMS: 7,
  CUSTOMER_PAYMENT_OPTIONS: 8,
  LEGAL: 9,
  EMAIL: 10,
  CART_CHECKOUT: 11,
  BYPASS_CHECKOUT: 12,
  ANALYTICS: 13,
  CUSTOMER_OPTIONS: 14,
  LANDING_PAGE: 15,
  MARKETING: 16,
  SHOPPING: 17,
  IPP_CONFIGURATION: 18,
  SFP_CONFIGURATION: 19,
  INTERNAL: 20,
  LOYALTY_SETUP: 21,
  CARROT_ADS: 22,
  REVIEW: 23,
} as const

export const WEB_STEP_MAPPING = {
  STOREFRONT_BRANDING: 0,
  DOMAIN: 1,
  FULFILLMENT: 2,
  IDENTITY: 3,
  PRIVACY: 4,
  CUSTOMER_EXPERIENCE: 5,
  LOYALTY_SETUP: 6,
  LOYALTY_PROGRAM: 7,
  CMS: 8,
  CUSTOMER_PAYMENT_OPTIONS: 9,
  LEGAL: 10,
  EMAIL: 11,
  CART_CHECKOUT: 12,
  ANALYTICS: 13,
  EXTEND_CONFIGURATIONS: 14,
  REVIEW: 15,
} as const

export const IOS_STEP_MAPPING = {
  IOS_ACCESS: 0,
  MOBILE: 1,
  IOS: 2,
  REVIEW: 3,
} as const

export const ANDROID_STEP_MAPPING = {
  ANDROID_ACCESS: 0,
  ANDROID_MANAGEMENT_ACCESS: 1,
  MOBILE: 2,
  ANDROID: 3,
  REVIEW: 4,
} as const

export const WEB_ATTRIBUTE_GROUPS = [
  'import_existing_site',
  'storefront_branding',
  'domain',
  'fulfillment',
  'identity',
  'privacy',
  'additional_branding',
  'customer_experience',
  'loyalty_setup',
  'loyalty_program',
  'cms',
  'customer_payment_options',
  'legal',
  'email',
  'cart_checkout',
  'analytics',
  'extend_configurations',
  'sfp_configuration',
  'carrot_ads',
]

export const IOS_ATTRIBUTE_GROUPS = ['ios_access', 'mobile', 'ios']

export const ANDROID_ATTRIBUTE_GROUPS = [
  'android_access',
  'android_management_access',
  'mobile',
  'android',
]

export const ALL_ATTRIBUTE_GROUPS = [
  ...WEB_ATTRIBUTE_GROUPS,
  ...IOS_ATTRIBUTE_GROUPS,
  ...ANDROID_ATTRIBUTE_GROUPS,
]

export const attributeGroupsByWhitelabelSite = (whitelabelSite: WhitelabelSiteData) => {
  const mapping: Record<string, string[]> = {
    web: WEB_ATTRIBUTE_GROUPS,
    ios: IOS_ATTRIBUTE_GROUPS,
    android: ANDROID_ATTRIBUTE_GROUPS,
  }

  return mapping[whitelabelSite?.type] ?? []
}

export const useDisabledAttributes = (
  whitelabelSite: WhitelabelSiteData,
  approvalRequests: ApprovalRequestData[],
  isAdmin: boolean
) => {
  const isWhitelabelSiteInReview = whitelabelSite?.workflowState === 'in_review'

  const deniedAttributes = (approvalRequests ?? [])
    .filter(request => request.status === 'denied')
    .map(request => request.attributeName)

  const hasModifyAccess = useHasModifyAccess({ isModifyingAdmin: isAdmin })

  const isAttributeDisabled = (name: string) => {
    if (!hasModifyAccess) {
      return true
    }

    return isWhitelabelSiteInReview && !deniedAttributes.includes(name)
  }

  const pendingAttributes = (approvalRequests ?? [])
    .filter(request => request.status === 'pending')
    .map(request => request.attributeName)

  const isAttributePending = (name: string) => pendingAttributes.includes(name)

  return {
    isWhitelabelSiteInReview,
    deniedAttributes,
    isAttributeDisabled,
    isAttributePending,
  }
}

export type WebStepMappingKeys = keyof typeof WEB_STEP_MAPPING
export type IosStepMappingKeys = keyof typeof IOS_STEP_MAPPING
export type AndroidStepMappingKeys = keyof typeof ANDROID_STEP_MAPPING

export type WebAdminStepMappingKeys = keyof typeof WEB_ADMIN_STEP_MAPPING

export const useStepTitleMap = (): Record<
  WebStepMappingKeys | IosStepMappingKeys | AndroidStepMappingKeys,
  {
    title: string
    subtitle?: string
  }
> => {
  const titleMapping = {
    IMPORT_EXISTING_SITE: {
      title: useDomainMessage('onboardingDomain.step.importExistingSite.title'),
    },
    STOREFRONT_BRANDING: {
      title: useDomainMessage('onboardingDomain.step.storefrontBranding.title'),
    },
    DOMAIN: {
      title: useDomainMessage('onboardingDomain.step.domain.title'),
    },
    FULFILLMENT: {
      title: useDomainMessage('onboardingDomain.step.fulfillment.title'),
    },
    IDENTITY: {
      title: useDomainMessage('onboardingDomain.step.identity.title'),
    },
    PRIVACY: {
      title: useDomainMessage('onboardingDomain.step.privacy.title'),
    },
    ADDITIONAL_BRANDING: {
      title: useDomainMessage('onboardingDomain.step.additional_branding.title'),
    },
    CUSTOMER_EXPERIENCE: {
      title: useDomainMessage('onboardingDomain.step.customerExperience.title'),
    },
    LOYALTY_SETUP: {
      title: useDomainMessage('onboardingDomain.step.loyaltySetup.title'),
    },
    LOYALTY_PROGRAM: {
      title: useDomainMessage('onboardingDomain.step.loyaltyProgram.title'),
      subtitle: useDomainMessage('onboardingDomain.step.loyaltyProgram.subtitle'),
    },
    CMS: {
      title: useDomainMessage('onboardingDomain.step.cms.title'),
    },
    CUSTOMER_PAYMENT_OPTIONS: {
      title: useDomainMessage('onboardingDomain.step.customerPaymentOptions.title'),
    },
    LEGAL: {
      title: useDomainMessage('onboardingDomain.step.legal.title'),
    },
    EMAIL: {
      title: useDomainMessage('onboardingDomain.step.email.title'),
    },
    CART_CHECKOUT: {
      title: useDomainMessage('onboardingDomain.step.cartCheckout.title'),
      subtitle: useDomainMessage('onboardingDomain.webSetup.form.cartCheckout.subtitle'),
    },
    BYPASS_CHECKOUT: {
      title: useDomainMessage('onboardingDomain.step.bypassCheckout.title'),
    },
    ANALYTICS: {
      title: useDomainMessage('onboardingDomain.step.analytics.title'),
    },
    CUSTOMER_OPTIONS: {
      title: useDomainMessage('onboardingDomain.step.customerOptions.title'),
    },
    LANDING_PAGE: {
      title: useDomainMessage('onboardingDomain.step.landingPage.title'),
    },
    MARKETING: {
      title: useDomainMessage('onboardingDomain.step.marketing.title'),
    },
    SHOPPING: {
      title: useDomainMessage('onboardingDomain.step.shopping.title'),
    },
    IPP_CONFIGURATION: {
      title: useDomainMessage('onboardingDomain.step.ippConfiguration.title'),
    },
    SFP_CONFIGURATION: {
      title: useDomainMessage('onboardingDomain.step.sfpConfiguration.title'),
    },
    INTERNAL: {
      title: useDomainMessage('onboardingDomain.step.internal.title'),
    },
    EXTEND_CONFIGURATIONS: {
      title: useDomainMessage('onboardingDomain.step.extendConfigurations.title'),
    },
    CARROT_ADS: {
      title: useDomainMessage('onboardingDomain.step.carrotAds.title'),
    },
    REVIEW: {
      title: useDomainMessage('onboardingDomain.step.review.title'),
    },
    MOBILE: {
      title: useDomainMessage('onboardingDomain.step.appDetails.title'),
    },
    IOS: {
      title: useDomainMessage('onboardingDomain.step.ios.title'),
    },
    IOS_ACCESS: {
      title: useDomainMessage('onboardingDomain.step.iosAccess.title'),
    },
    ANDROID: {
      title: useDomainMessage('onboardingDomain.step.android.title'),
    },
    ANDROID_ACCESS: {
      title: useDomainMessage('onboardingDomain.step.androidAccess.title'),
    },
    ANDROID_MANAGEMENT_ACCESS: {
      title: useDomainMessage('onboardingDomain.step.androidManagementAccess.title'),
    },
  }

  return titleMapping
}

export const useGetWhitelabelSiteStateLabelMap = () =>
  useDomainMessages({
    [WhitelabelSiteWorkflowState.initialized]: 'onboardingDomain.siteState.initialized',
    [WhitelabelSiteWorkflowState.initialEmailSent]: 'onboardingDomain.siteState.initialEmailSent',
    [WhitelabelSiteWorkflowState.inProgress]: 'onboardingDomain.siteState.inProgress',
    [WhitelabelSiteWorkflowState.inReview]: 'onboardingDomain.siteState.inReview',
    [WhitelabelSiteWorkflowState.published]: 'onboardingDomain.siteState.published',
  })

// Maps attribute subgroup names to runbook information
export const ATTRIBUTE_SUBGROUP_RUNBOOKS_MAP: Record<string, string> = {
  'Order Operational Dashboard': 'https://go/orders-operational-dashboard-runbook',
  'Scanning Items': 'https://go/scan-item-barcode-runbook',
  'SMS Short Links':
    'https://instacart.atlassian.net/wiki/spaces/ENTSO/pages/3898802914/SFX+I+Enable+V4+Checkout#SMS-Short-Links',
  'Dynamic Slots': 'https://go/campaign-tooling-runbook#Enable-and-Create-Dynamic-Slots',
  'Guest User Mode': 'https://go/guest-user-mode-runbook',
  'IC+ Enablement':
    'https://instacart.atlassian.net/wiki/spaces/ENTSO/pages/3901194391/SFX+Instacart+Integrations',
  'Gift Cards': 'https://go/cpra-privacy-runbook',
  'Loyalty Cards':
    'https://instacart.atlassian.net/wiki/spaces/ENTSO/pages/4627791966/SFX+Hide+Loyalty+Cards+from+Accounts+Menu',
  'Priority Service': 'https://go/sfx-v4-checkout-runbook#Disable-Priority-Service-Options',
  'No Rush Service': 'https://go/sfx-v4-checkout-runbook#Disable-No-Rush-Service-Options',
  'Initial Free Delivery': 'https://go/free-delivery-runbook',
  'Cart Heading': 'https://go/sfx-v4-checkout-runbook#Cart-Heading',
  'Checkout Recommendations': 'https://go/sfx-v4-checkout-runbook#Checkout-Aisles',
  'SMS Marketing Opt-In': 'https://go/sfx-v4-checkout-runbook#Hide-SMS-Marketing-Opt-In',
  'Multi-Step Tipping': 'https://go/sfx-v4-checkout-runbook#Multi-Step-Tipping',
  Gifting: 'https://go/sfx-v4-checkout-runbook#Gifting',
  'Checkout Totals Configuration':
    'https://go/sfx-v4-checkout-runbook#Checkout-Totals-Configuration',
}

export const enum ONBOARDING_ATTRIBUTE_VISIBILITY_TIERS {
  RETAILER_FORM = 'retailer_form',
  ADMIN = 'admin',
}
